import { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash/fp';
import { EditorState, Compartment } from '@codemirror/state';
import {
    EditorView,
    highlightActiveLine,
    drawSelection,
    keymap,
    lineNumbers,
    highlightActiveLineGutter
} from '@codemirror/view';
import { json, jsonLanguage } from '@codemirror/lang-json';
import { foldGutter, syntaxHighlighting } from '@codemirror/language';
import { defaultKeymap } from '@codemirror/commands';
import classnames from 'classnames/bind';
import { defaultClassHighlightStyles } from './HighlightStyles.js';
import { useValueUpdater } from './useValueUpdater.js';
import { getPath, getType } from './jsonViewerUtils.js';
import * as styles from './JsonCodeViewer.css';

const cx = classnames.bind(styles);

export function JsonCodeViewer({
    value,
    onCursorPositionChange = noop,
    displayAsText = false,
    className = null
}) {
    const parent = useRef();
    const previousCursorPosition = useRef();
    const [lineWrapping] = useState(() => new Compartment());

    const tree = useMemo(() => jsonLanguage.parser.parse(value), [value]);

    const handleCurrentLineChange = (position) => {
        if (previousCursorPosition.current === position) {
            return null;
        }
        previousCursorPosition.current = position;
        let node = tree.resolveInner(position, -1);
        if (node.type.name === 'Array' || node.type.name === 'Property') {
            node = tree.resolveInner(position + 1, -1);
        }

        const path = getPath(node, value);
        const type = getType(node, value);
        onCursorPositionChange({ type, path });
    };
    const [editor] = useState(() => {
        const state = EditorState.create({
            doc: '',
            extensions: [
                lineNumbers(),
                highlightActiveLineGutter(),
                highlightActiveLine(),
                foldGutter(),
                drawSelection(),
                syntaxHighlighting(defaultClassHighlightStyles),
                json(),
                lineWrapping.of(displayAsText ? EditorView.lineWrapping : []),
                EditorState.readOnly.of(true),
                keymap.of(defaultKeymap),
                EditorView.updateListener.of(() => {
                    if (!displayAsText) {
                        const endOfLinePos =
                            editor.state.doc.lineAt(editor.state.selection.main.to).to - 1;
                        handleCurrentLineChange(endOfLinePos);
                    }
                })
            ]
        });

        return new EditorView({ state });
    });

    useEffect(() => {
        const container = parent.current;
        const child = editor.dom;
        container.appendChild(child);
        return () => container.removeChild(child);
    }, [editor]);

    useValueUpdater(editor, value);

    useEffect(() => {
        editor.dispatch({
            effects: lineWrapping.reconfigure(displayAsText ? EditorView.lineWrapping : [])
        });
    }, [editor, displayAsText, lineWrapping]);

    return (
        <div className={cx('base', className)}>
            <div className={styles.editor} ref={parent} />
        </div>
    );
}

JsonCodeViewer.propTypes = {
    value: PropTypes.string.isRequired,
    onCursorPositionChange: PropTypes.func,
    displayAsText: PropTypes.bool,
    className: PropTypes.string
};
